import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getEquipmentById, getEquipmentHistory, uploadEquipmentImage, deleteEquipmentImage } from "@/lib/api";
import { toast } from "sonner";
import { 
  ArrowLeft, Camera, Upload, Trash2, Image, ZoomIn, 
  Clock, Package, ArrowLeftRight, RotateCcw, ShoppingCart,
  FileText, Printer
} from "lucide-react";

export default function EquipmentDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [equipment, setEquipment] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [description, setDescription] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const [viewImage, setViewImage] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [equipmentRes, historyRes] = await Promise.all([
        getEquipmentById(id),
        getEquipmentHistory(id)
      ]);
      setEquipment(equipmentRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      toast.error("Equipment nicht gefunden");
      navigate("/equipment");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Bitte nur Bilddateien auswählen");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Bild darf maximal 5MB groß sein");
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      setPreviewImage(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!previewImage) return;
    setUploading(true);
    try {
      await uploadEquipmentImage(id, {
        image_data: previewImage,
        description: description
      });
      toast.success(t("detail.photoUploaded"));
      setPreviewImage(null);
      setDescription("");
      loadData();
    } catch (error) {
      toast.error("Fehler beim Hochladen");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm(t("detail.deletePhoto"))) return;
    try {
      await deleteEquipmentImage(id, imageId);
      toast.success(t("detail.photoDeleted"));
      loadData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (value, currency = "TND") => {
    if (currency === "TND") {
      return `${(value || 0).toFixed(3)} TND`;
    }
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency || "EUR"
    }).format(value || 0);
  };

  const getTimelineIcon = (type) => {
    switch (type) {
      case "created": return <Package className="w-4 h-4" />;
      case "loan": return <ArrowLeftRight className="w-4 h-4" />;
      case "return": return <RotateCcw className="w-4 h-4" />;
      case "sale": return <ShoppingCart className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getTimelineColor = (type) => {
    switch (type) {
      case "created": return "bg-blue-100 text-blue-600";
      case "loan": return "bg-amber-100 text-amber-600";
      case "return": return "bg-emerald-100 text-emerald-600";
      case "sale": return "bg-purple-100 text-purple-600";
      default: return "bg-slate-100 text-slate-600";
    }
  };

  const printReport = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">{t("common.loading")}</div>
      </div>
    );
  }

  if (!equipment) return null;

  return (
    <div data-testid="equipment-detail-page" className="print:p-4">
      <div className="mb-6 print:hidden">
        <Button
          variant="ghost"
          onClick={() => navigate("/equipment")}
          className="gap-2 text-slate-600 hover:text-slate-900"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("detail.backToOverview")}
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="page-title">{equipment.name}</h1>
          <p className="page-subtitle font-mono">{equipment.serial_number}</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button variant="outline" onClick={printReport} className="gap-2">
            <Printer className="w-4 h-4" />
            Drucken
          </Button>
        </div>
      </div>

      <Tabs defaultValue="details" className="space-y-6">
        <TabsList className="print:hidden">
          <TabsTrigger value="details" className="gap-2">
            <FileText className="w-4 h-4" />
            {t("detail.details")}
          </TabsTrigger>
          <TabsTrigger value="photos" className="gap-2">
            <Image className="w-4 h-4" />
            {t("detail.photos")} ({equipment.images?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <Clock className="w-4 h-4" />
            Historie
          </TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Equipment-Informationen</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Seriennummer</p>
                  <p className="font-mono font-medium">{equipment.serial_number}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Kategorie</p>
                  <p className="font-medium">{equipment.category_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Zustand</p>
                  <p className="font-medium">{equipment.condition}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <span className={
                    equipment.status === "Verfügbar" ? "badge-success" :
                    equipment.status === "Ausgeliehen" ? "badge-warning" : "badge-neutral"
                  }>
                    {equipment.status}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Kaufdatum</p>
                  <p className="font-medium">{formatDate(equipment.purchase_date).split(",")[0]}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Kaufpreis</p>
                  <p className="font-medium">{formatCurrency(equipment.purchase_price, equipment.currency)}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Kauf-Informationen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-slate-500">Rechnungsnummer</p>
                  <p className="font-medium">{equipment.invoice_number || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Händler / Lieferant</p>
                  <p className="font-medium">{equipment.vendor || "-"}</p>
                </div>
                {equipment.notes && (
                  <div>
                    <p className="text-sm text-slate-500">Notizen</p>
                    <p className="text-sm bg-slate-50 p-3 rounded-lg mt-1">{equipment.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Current Loan Info */}
            {history?.loans?.find(l => l.status === "Ausgeliehen") && (
              <Card className="border-amber-200 bg-amber-50/50 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg text-amber-800">Aktuelle Ausleihe</CardTitle>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const activeLoan = history.loans.find(l => l.status === "Ausgeliehen");
                    return (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-sm text-amber-700">Mitarbeiter</p>
                          <p className="font-medium">{activeLoan.employee_name}</p>
                        </div>
                        <div>
                          <p className="text-sm text-amber-700">Ausgeliehen am</p>
                          <p className="font-medium">{formatDate(activeLoan.loan_date).split(",")[0]}</p>
                        </div>
                        <div>
                          <p className="text-sm text-amber-700">Zustand bei Ausleihe</p>
                          <p className="font-medium">{activeLoan.condition_at_loan}</p>
                        </div>
                        <div>
                          <p className="text-sm text-amber-700">Geplante Rückgabe</p>
                          <p className="font-medium">{activeLoan.expected_return_date ? formatDate(activeLoan.expected_return_date).split(",")[0] : "-"}</p>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Photos Tab */}
        <TabsContent value="photos">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between print:hidden">
              <CardTitle className="text-lg">{t("detail.photos")}</CardTitle>
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="gap-2"
                data-testid="upload-photo-btn"
              >
                <Camera className="w-4 h-4" />
                {t("detail.addPhoto")}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />
            </CardHeader>
            <CardContent>
              {/* Upload Preview */}
              {previewImage && (
                <div className="mb-6 p-4 bg-slate-50 rounded-xl border border-slate-200 print:hidden">
                  <div className="flex gap-4">
                    <img src={previewImage} alt="Vorschau" className="w-32 h-32 object-cover rounded-lg" />
                    <div className="flex-1 space-y-3">
                      <div>
                        <Label className="form-label">{t("common.description")}</Label>
                        <Input
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="z.B. Zustand bei Kauf, Kratzer links..."
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleUpload} disabled={uploading} className="gap-2">
                          <Upload className="w-4 h-4" />
                          {uploading ? "Lädt..." : "Hochladen"}
                        </Button>
                        <Button variant="outline" onClick={() => { setPreviewImage(null); setDescription(""); }}>
                          {t("common.cancel")}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Image Gallery */}
              {!equipment.images || equipment.images.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Image className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>{t("detail.noPhotos")}</p>
                  <p className="text-sm mt-1">{t("detail.addPhotosHint")}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {equipment.images.map((image) => (
                    <div
                      key={image.id}
                      className="group relative rounded-xl overflow-hidden border border-slate-200 bg-slate-50"
                    >
                      <img
                        src={image.data}
                        alt={image.description || "Equipment Foto"}
                        className="w-full h-40 object-cover cursor-pointer"
                        onClick={() => setViewImage(image)}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 print:hidden">
                        <Button variant="secondary" size="icon" className="h-8 w-8" onClick={() => setViewImage(image)}>
                          <ZoomIn className="w-4 h-4" />
                        </Button>
                        <Button variant="destructive" size="icon" className="h-8 w-8" onClick={() => handleDeleteImage(image.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      {image.description && (
                        <div className="p-2 bg-white border-t border-slate-100">
                          <p className="text-xs text-slate-600 truncate">{image.description}</p>
                        </div>
                      )}
                      <div className="absolute top-2 right-2 text-xs bg-black/50 text-white px-2 py-0.5 rounded">
                        {formatDate(image.uploaded_at).split(",")[0]}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Equipment-Historie</CardTitle>
            </CardHeader>
            <CardContent>
              {!history?.timeline || history.timeline.length === 0 ? (
                <p className="text-slate-500 text-center py-8">Keine Historie vorhanden</p>
              ) : (
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-200" />
                  
                  <div className="space-y-6">
                    {history.timeline.map((event, index) => (
                      <div key={index} className="relative flex gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 z-10 ${getTimelineColor(event.type)}`}>
                          {getTimelineIcon(event.type)}
                        </div>
                        <div className="flex-1 bg-slate-50 rounded-lg p-4 border border-slate-100">
                          <div className="flex items-start justify-between">
                            <p className="font-medium text-slate-900">{event.description}</p>
                            <span className="text-sm text-slate-500">{formatDate(event.date)}</span>
                          </div>
                          {event.details && (
                            <div className="mt-2 text-sm text-slate-600 space-y-1">
                              {event.details.purchase_price && (
                                <p>Kaufpreis: {formatCurrency(event.details.purchase_price, event.details.currency)}</p>
                              )}
                              {event.details.vendor && <p>Händler: {event.details.vendor}</p>}
                              {event.details.invoice_number && <p>Rechnung: {event.details.invoice_number}</p>}
                              {event.details.condition_at_loan && <p>Zustand: {event.details.condition_at_loan}</p>}
                              {event.details.condition_at_return && <p>Zustand bei Rückgabe: {event.details.condition_at_return}</p>}
                              {event.details.return_notes && <p>Anmerkung: {event.details.return_notes}</p>}
                              {event.details.sale_price && <p>Verkaufspreis: {formatCurrency(event.details.sale_price, "TND")}</p>}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Image View Dialog */}
      <Dialog open={!!viewImage} onOpenChange={() => setViewImage(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{viewImage?.description || "Equipment Foto"}</DialogTitle>
          </DialogHeader>
          {viewImage && (
            <div className="mt-4">
              <img
                src={viewImage.data}
                alt={viewImage.description || "Equipment Foto"}
                className="w-full max-h-[70vh] object-contain rounded-lg"
              />
              <p className="text-sm text-slate-500 mt-2">
                {t("detail.uploadedAt")}: {formatDate(viewImage.uploaded_at)}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
